import React, { useState } from "react";
import { Box, Button, ButtonBase, Modal, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { FilePreview } from "../FilesInput/FilePreview";
import { isImage, isPDF } from "../../helpers/files";

interface Attachment {
  id: number;
  fileName: string;
  downloadLink: string;
  s3ResourceURL?: string;
  s3ResourceKey?: string;
}

interface AttachmentPreviewProps {
  attachment: Attachment;
}

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({ attachment }) => {
  const [open, setOpen] = useState(false);

  const fileIsPDF = isPDF(attachment.fileName);
  const fileIsImage = isImage(attachment.fileName);

  const renderVisualPreview = () => {
    if (fileIsImage) {
      return (
        <img
          src={attachment.downloadLink}
          alt={attachment.fileName}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block"
          }}
        />
      );
    }

    return (
      <Typography sx={{ fontSize: "2.5rem", lineHeight: 1 }}>
        {fileIsPDF ? <PictureAsPdfIcon /> : <AttachFileIcon />}
      </Typography>
    );
  };

  const getButtonLabel = () => {
    if (fileIsImage) return "Preview";
    if (fileIsPDF) return "View PDF";
    return "View File";
  };

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box>
          <FilePreview
            onClose={() => setOpen(false)}
            fileDetails={{ name: attachment.fileName, url: attachment.downloadLink }}
          />
        </Box>
      </Modal>

      <Box
        sx={{
          width: 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 1
        }}
      >
        <ButtonBase
          onClick={() => setOpen(true)}
          sx={{
            width: "100%",
            height: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: fileIsImage ? "none" : "1px solid #ddd",
            borderRadius: "6px",
            backgroundColor: fileIsImage ? "transparent" : "#fafafa",
            overflow: "hidden",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.9,
              borderColor: "primary.main"
            }
          }}
        >
          {renderVisualPreview()}
        </ButtonBase>

        <Typography
          sx={{
            fontSize: "0.7rem",
            color: "text.secondary",
            wordBreak: "break-all",
            height: "2.5em",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
          }}
        >
          {attachment.fileName}
        </Typography>

        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "primary.main",
            textTransform: "none",
            fontSize: "0.75rem"
          }}
        >
          <VisibilityIcon sx={{ fontSize: "0.9rem" }} />
          <span>{getButtonLabel()}</span>
        </Button>
      </Box>
    </>
  );
};

export default AttachmentPreview;
